/* eslint-disable @typescript-eslint/no-unsafe-assignment */
// Shared inline JSON syntax highlighter — zero dependencies

type TokenType = 'key' | 'string' | 'number' | 'boolean' | 'null' | 'punct' | 'space';
interface Token {
  type: TokenType;
  text: string;
}

function tokenizeJson(text: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < text.length) {
    const ch = text[i]!;
    if (/\s/.test(ch)) {
      let ws = '';
      while (i < text.length && /\s/.test(text[i]!)) ws += text[i++]!;
      tokens.push({ type: 'space', text: ws });
      continue;
    }
    if ('{}[],:'.includes(ch)) {
      tokens.push({ type: 'punct', text: ch });
      i++;
      continue;
    }
    if (ch === '"') {
      let str = '"';
      i++;
      while (i < text.length) {
        if (text[i]! === '\\') {
          str += text[i]! + (text[i + 1] ?? '');
          i += 2;
        } else if (text[i]! === '"') {
          str += '"';
          i++;
          break;
        } else {
          str += text[i++]!;
        }
      }
      // peek past whitespace: if next char is ':', it's an object key
      let j = i;
      while (j < text.length && text[j]! === ' ') j++;
      tokens.push({ type: j < text.length && text[j]! === ':' ? 'key' : 'string', text: str });
      continue;
    }
    if (/[-\d]/.test(ch)) {
      let num = '';
      while (i < text.length && /[-\d.eE+]/.test(text[i]!)) num += text[i++]!;
      tokens.push({ type: 'number', text: num });
      continue;
    }
    if (text.slice(i, i + 4) === 'true') {
      tokens.push({ type: 'boolean', text: 'true' });
      i += 4;
      continue;
    }
    if (text.slice(i, i + 5) === 'false') {
      tokens.push({ type: 'boolean', text: 'false' });
      i += 5;
      continue;
    }
    if (text.slice(i, i + 4) === 'null') {
      tokens.push({ type: 'null', text: 'null' });
      i += 4;
      continue;
    }
    tokens.push({ type: 'punct', text: ch });
    i++;
  }
  return tokens;
}

// Accessible, readable colors against a dark background
const TOKEN_COLOR: Record<TokenType, string> = {
  key: '#7dd3fc', // sky-300 — keys stand out
  string: '#86efac', // green-300 — string values
  number: '#fbbf24', // amber-400 — numbers
  boolean: '#c084fc', // purple-400
  null: '#94a3b8', // slate-400
  punct: '#475569', // slate-600
  space: 'inherit',
};

/**
 * Renders a JSON value with full syntax coloring.
 * set `inline` to render without line breaks (for table cells).
 */
export function JsonHighlight({
  value,
  inline = false,
}: {
  value: unknown;
  inline?: boolean;
}): import('react/jsx-runtime').JSX.Element {
  const text = inline ? JSON.stringify(value) : JSON.stringify(value, null, 2);
  const tokens = tokenizeJson(text);
  return (
    <pre
      className={
        inline
          ? 'text-[11px] font-mono whitespace-nowrap'
          : 'text-[11px] font-mono whitespace-pre-wrap break-all leading-relaxed'
      }
    >
      {tokens.map((t, idx): import('react/jsx-runtime').JSX.Element => (
        <span key={idx} style={{ color: TOKEN_COLOR[t.type] }}>
          {t.text}
        </span>
      ))}
    </pre>
  );
}

/**
 * Tries to parse `text` as JSON. If it looks like JSON (starts with { or [),
 * renders it with syntax highlighting. Otherwise returns plain text.
 */
export function SmartMessage({
  text,
  className,
}: {
  text: string;
  className?: string | undefined;
}): import('react/jsx-runtime').JSX.Element {
  const trimmed = text.trim();
  if ((trimmed.startsWith('{') || trimmed.startsWith('[')) && trimmed.length > 2) {
    try {
      const parsed = JSON.parse(trimmed);
      // eslint-disable-next-line react-hooks/error-boundaries
      return <JsonHighlight value={parsed} inline />;
    } catch {
      // not valid JSON — fall through
    }
  }
  return <span className={className}>{text}</span>;
}
